import {
  ComponentProps,
  Fragment,
  ReactNode,
  useContext,
  useEffect,
} from "react";
import { SegmentGroup, useSegmentGroupContext } from "@ark-ui/react";
import SidebarContext from "./SidebarContext";
import withTestId from "../../utils/withTestId";

export type SideBarItemProps<T extends React.ElementType | undefined> =
  (T extends React.ElementType
    ? Omit<ComponentProps<T>, "value" | "title">
    : never) & {
    title: string | React.ReactNode;
    value: string;
    disabled?: boolean;
    as?: T;
    /** use this property to control the selected item of the sidebar, for example by computing from the url*/
    isActive?: boolean;
    tagComponent?: ReactNode;
  };

function SideBarItem<T extends React.ElementType | undefined>({
  title,
  value,
  disabled,
  as,
  isActive,
  onClick,
  tagComponent,
  ...props
}: SideBarItemProps<T>) {
  const renderedAsCustomElement = as !== undefined;
  const AsTag = as ?? Fragment;
  const context = useSegmentGroupContext();
  const { open } = useContext(SidebarContext);

  useEffect(() => {
    if (isActive && context.value !== value) {
      context.setValue(value);
    }
  }, [isActive, context, value]);

  return (
    <SegmentGroup.Item
      {...props}
      value={value}
      disabled={disabled}
      asChild={renderedAsCustomElement}
      onClick={(e) => {
        if (disabled) {
          return;
        }

        if (renderedAsCustomElement && !disabled) {
          context.setValue(value);
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        onClick?.(e);
      }}
      tabIndex="0"
    >
      <AsTag>
        {typeof title === "string" ? (
          <SegmentGroup.ItemText>{title}</SegmentGroup.ItemText>
        ) : (
          title
        )}
        {as === undefined && <SegmentGroup.ItemHiddenInput />}
        {open && tagComponent}
      </AsTag>
    </SegmentGroup.Item>
  );
}

export default withTestId("nav")(SideBarItem);
